import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { useEffect, useState } from 'react';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link as LinkIcon,
  Undo,
  Redo,
  Code,
} from 'lucide-react';

const MenuButton = ({ onClick, isActive, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    tabIndex={-1}
    className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
      isActive ? 'bg-gray-200 text-aa-primary' : 'text-gray-600'
    }`}
    title={title}
  >
    {children}
  </button>
);

const MenuBar = ({ editor, showHtmlView, onToggleHtml }) => {
  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 border-b bg-gray-50">
      <MenuButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        title="Bold"
      >
        <Bold className="w-4 h-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        title="Italic"
      >
        <Italic className="w-4 h-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive('underline')}
        title="Underline"
      >
        <UnderlineIcon className="w-4 h-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        isActive={editor.isActive('strike')}
        title="Strikethrough"
      >
        <Strikethrough className="w-4 h-4" />
      </MenuButton>

      <div className="w-px h-5 bg-gray-300 mx-1" />

      <MenuButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        title="Bullet List"
      >
        <List className="w-4 h-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        title="Numbered List"
      >
        <ListOrdered className="w-4 h-4" />
      </MenuButton>

      <div className="w-px h-5 bg-gray-300 mx-1" />

      <MenuButton
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        isActive={editor.isActive({ textAlign: 'left' })}
        title="Align Left"
      >
        <AlignLeft className="w-4 h-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        isActive={editor.isActive({ textAlign: 'center' })}
        title="Align Center"
      >
        <AlignCenter className="w-4 h-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        isActive={editor.isActive({ textAlign: 'right' })}
        title="Align Right"
      >
        <AlignRight className="w-4 h-4" />
      </MenuButton>

      <div className="w-px h-5 bg-gray-300 mx-1" />

      <MenuButton
        onClick={addLink}
        isActive={editor.isActive('link')}
        title="Add Link"
      >
        <LinkIcon className="w-4 h-4" />
      </MenuButton>

      <div className="w-px h-5 bg-gray-300 mx-1" />

      <MenuButton
        onClick={() => editor.chain().focus().undo().run()}
        title="Undo"
      >
        <Undo className="w-4 h-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().redo().run()}
        title="Redo"
      >
        <Redo className="w-4 h-4" />
      </MenuButton>

      {/* HTML Toggle - pushed to the right */}
      <div className="flex-1" />
      <MenuButton
        onClick={onToggleHtml}
        isActive={showHtmlView}
        title="View HTML Source"
      >
        <Code className="w-4 h-4" />
      </MenuButton>
    </div>
  );
};

export default function RichTextEditor({ value, onChange, placeholder }) {
  const [showHtmlView, setShowHtmlView] = useState(false);
  const [htmlSource, setHtmlSource] = useState(value || '');
  
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Configure paragraph to have no default margin
        paragraph: {
          HTMLAttributes: {
            style: 'margin: 0;',
          },
        },
        // Use hard breaks for single Enter (Shift+Enter for paragraph)
        hardBreak: true,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
      setHtmlSource(html);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none min-h-[200px] max-h-[40vh] overflow-y-auto p-3 focus:outline-none [&_p]:my-0',
      },
      // Make Enter create a <br> instead of a new paragraph
      handleKeyDown: (view, event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          // Insert a hard break (br) on Enter
          const { state, dispatch } = view;
          const { tr } = state;
          const hardBreak = state.schema.nodes.hardBreak;
          if (hardBreak) {
            dispatch(tr.replaceSelectionWith(hardBreak.create()).scrollIntoView());
            return true;
          }
        }
        return false;
      },
    },
  });

  // Update editor content when value changes externally
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
      setHtmlSource(value || '');
    }
  }, [value, editor]);

  const handleHtmlChange = (e) => {
    const newHtml = e.target.value;
    setHtmlSource(newHtml);
    onChange(newHtml);
    if (editor) {
      editor.commands.setContent(newHtml);
    }
  };

  const toggleHtmlView = () => {
    if (showHtmlView && editor) {
      // Switching from HTML to visual - update editor with current HTML
      editor.commands.setContent(htmlSource);
    }
    setShowHtmlView(!showHtmlView);
  };

  return (
    <div className="border rounded-md overflow-hidden bg-white">
      <MenuBar editor={editor} showHtmlView={showHtmlView} onToggleHtml={toggleHtmlView} />
      {showHtmlView ? (
        <textarea
          value={htmlSource}
          onChange={handleHtmlChange}
          className="w-full min-h-[200px] max-h-[40vh] p-3 font-mono text-xs bg-gray-900 text-green-400 focus:outline-none resize-y"
          placeholder="Enter HTML..."
          spellCheck={false}
        />
      ) : (
        <EditorContent editor={editor} />
      )}
      <style>{`
        .ProseMirror {
          min-height: 200px;
          max-height: 40vh;
          overflow-y: auto;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          color: #adb5bd;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .ProseMirror:focus {
          outline: none;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.5rem;
        }
        .ProseMirror a {
          color: #2563eb;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
}
